/**
 * Hook: Facility CRUD actions with toast feedback and cache invalidation.
 * Single Responsibility: call services, refetch, show toasts.
 * Depends on: showToast (from FeedbackContext), refetchFacilities from useSetOrganizationStructure.
 */

import { useCallback } from 'react';
import {
  createFacility,
  updateFacility,
  deleteFacility,
  duplicateFacility,
} from '../../../services/facilitiesService';

/** Toast configuration: single source of truth for duration and titles */
const TOAST_CONFIG = {
  DURATION_MS: 4000,
  TITLE_SUCCESS: 'Success',
  TITLE_ERROR: 'Error',
};

function buildDeleteSuccessCopy(message, deletedLocationsCount) {
  if (message) return message;
  const count = deletedLocationsCount ?? 0;
  const suffix = count !== 1 ? 's' : '';
  return `Facility and ${count} area${suffix} deleted successfully`;
}

function buildDuplicateSuccessCopy(newName, data) {
  let copy = `Facility "${newName}" duplicated successfully`;
  const parts = [];
  const {
    duplicatedLocationsCount,
    duplicatedFacilityStandards,
    duplicatedLocationStandardsCount,
  } = data || {};
  if (duplicatedLocationsCount > 0) {
    parts.push(
      `${duplicatedLocationsCount} area${duplicatedLocationsCount !== 1 ? 's' : ''}`,
    );
  }
  if (duplicatedFacilityStandards) parts.push('facility standards');
  if (duplicatedLocationStandardsCount > 0) {
    parts.push(
      `${duplicatedLocationStandardsCount} area standard${duplicatedLocationStandardsCount !== 1 ? 's' : ''}`,
    );
  }
  if (parts.length > 0) copy += ` with ${parts.join(', ')}`;
  return copy;
}

function showSuccessToast(showToast, copy, duration = TOAST_CONFIG.DURATION_MS) {
  showToast({
    variant: 'success',
    title: TOAST_CONFIG.TITLE_SUCCESS,
    copy,
    duration,
  });
}

export function useFacilityActions(showToast, refetchFacilities) {
  const handleAddFacility = useCallback(
    async (data) => {
      const response = await createFacility(data);
      const newFacility = response.data;
      await refetchFacilities();
      showSuccessToast(
        showToast,
        `${newFacility.name} has been added to your organization successfully`,
      );
    },
    [showToast, refetchFacilities],
  );

  const handleUpdateFacility = useCallback(
    async (id, data) => {
      await updateFacility(id, data);
      await refetchFacilities();
      showSuccessToast(showToast, `${data.name} has been updated successfully`);
    },
    [showToast, refetchFacilities],
  );

  const handleDeleteFacility = useCallback(
    async (facilityId) => {
      const response = await deleteFacility(facilityId);
      const { message, deletedLocationsCount } = response.data;
      await refetchFacilities();
      showSuccessToast(
        showToast,
        buildDeleteSuccessCopy(message, deletedLocationsCount),
      );
    },
    [showToast, refetchFacilities],
  );

  const showErrorToast = useCallback(
    (defaultMessage) => (error) => {
      showToast({
        variant: 'error',
        title: TOAST_CONFIG.TITLE_ERROR,
        copy: error?.message || defaultMessage,
        duration: TOAST_CONFIG.DURATION_MS,
      });
    },
    [showToast],
  );

  const handleDuplicateConfirm = useCallback(
    async (facilityId, options, newName, onSuccess) => {
      const response = await duplicateFacility(facilityId, options);
      await refetchFacilities();
      showSuccessToast(
        showToast,
        buildDuplicateSuccessCopy(newName, response.data),
      );
      onSuccess?.();
    },
    [showToast, refetchFacilities],
  );

  return {
    handleAddFacility,
    handleUpdateFacility,
    handleDeleteFacility,
    handleDuplicateConfirm,
    showErrorToast,
  };
}
